import { Smartphone } from 'lucide-react';
import React, { useState, useEffect } from 'react';

interface LoaderProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  scale?: '0.25x' | '0.5x' | '0.75x' | '1x' | '1.25x' | '1.5x' | '1.75x' | '2x';
  fullScreen?: boolean;
  className?: string;
  title?: string;
  subtitle?: string;
  progress?: number;
  showProgress?: boolean;
}

const Loader: React.FC<LoaderProps> = React.memo(({
  size = 'lg',
  scale = '1x',
  fullScreen = false,
  className = '',
  title = '~Loading~',
  subtitle = 'Please wait while we prepare your trading experience',
  progress = 0,
  showProgress = false,
}) => {
  const [animatedProgress, setAnimatedProgress] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);

  // Animate progress from 1% to 100% when showProgress becomes true
  useEffect(() => {
    if (showProgress && !isAnimating) {
      setIsAnimating(true);
      setAnimatedProgress(1);
      
      // Animate from 1% to 100% over 2 seconds
      const duration = 2000; // 2 seconds
      const startTime = Date.now();
      const startProgress = 1;
      const endProgress = 100;
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progressRatio = Math.min(elapsed / duration, 1);
        
        // Use easeOutCubic for smooth deceleration
        const easeOutCubic = 1 - Math.pow(1 - progressRatio, 3);
        const currentProgress = startProgress + (endProgress - startProgress) * easeOutCubic;
        
        setAnimatedProgress(Math.round(currentProgress));
        
        if (progressRatio < 1) {
          requestAnimationFrame(animate);
        } else {
          setIsAnimating(false);
        }
      };
      
      requestAnimationFrame(animate);
    }
  }, [showProgress, isAnimating]);
  const sizeClasses = {
    xs: 'h-12 w-12',
    sm: 'h-16 w-16',
    md: 'h-24 w-24',
    lg: 'h-32 w-32',
    xl: 'h-40 w-40',
  };

  const scaleClasses = {
    '0.25x': 'scale-[0.25]',
    '0.5x': 'scale-50',
    '0.75x': 'scale-75',
    '1x': 'scale-100',
    '1.25x': 'scale-125',
    '1.5x': 'scale-150',
    '1.75x': 'scale-[1.75]',
    '2x': 'scale-[2]',
  };


  const LoaderContent = () => (
    <div className={`loader-container ${className} ${scaleClasses[scale]}`}>
      <div className={`loader-wrapper ${sizeClasses['sm']}`}>
        {/* Spinner Animation */}
        <div className="spinnerwrap">
          <div className="outerCircle"></div>
          <div className="innerCircle"></div>
          <div className="icon"></div>
          <div id="diamond-shield"></div>
        </div>
        
        {/* Logo Symbol */}
        <img
          src="/images/logo_symbol.svg"
          alt="MIAI Symbol"
          className="loader-logo"
        />
      </div>
      
      {/* Loading Text */}
      <div className="loader-text">
        <div className="loader-title">{title}</div>
        <div className="loader-subtitle">{subtitle}</div>
        
        {/* Progress Bar */}
        {showProgress && (
          <div className="w-full max-w-xs mt-4">
            <div className="bg-gray-700 rounded-full h-2 overflow-hidden shadow-inner">
              <div 
                className="bg-[#2AB7A6] h-full transition-all duration-300 ease-out rounded-full"
                style={{ width: `${Math.min(100, Math.max(0, animatedProgress))}%` }}
              >
              </div>
            </div>
            <div className="text-xs text-gray-400 mt-2 text-center font-medium transition-all duration-200">
              <span className="inline-block transform transition-transform duration-200 hover:scale-110">
                {animatedProgress}%
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (fullScreen) {
    return (
      <div className="loader-overlay">
        <LoaderContent />
      </div>
    );
  }

  return <LoaderContent />;
});

Loader.displayName = 'Loader';

export default Loader;
